<script setup lang="ts">
import { ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { useUserStore } from '@/modules/user/stores/user.store'
import type { SearchHit } from '../interfaces/search'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import SpinnerLoader from '@/shared/components/loader/SpinnerLoader.vue'

interface Props {
  title: string
  items: SearchHit[]
  loading?: boolean
  disabled?: boolean
}

const props = defineProps<Props>()
const model = defineModel<SearchHit>()
const isActive = ref(false)
const userStore = useUserStore()

const setDefaultValue = (): void => {
  if (props.items.length > 0) {
    const client = props.items.find((item) => item.clientKey === userStore.activeClient?.clientKey)
    if (client) {
      model.value = client
    } else {
      model.value = props.items[0]
    }
  }
}

const updateModel = (item: SearchHit) => {
  model.value = item
  isActive.value = false
}

setDefaultValue()
</script>

<template>
  <div class="relative" v-on-click-outside="() => (isActive = false)">
    <SpinnerLoader :loading="loading">
      <div class="flex items-center border border-gray-300" :class="{ 'border-dotted': disabled }">
        <BaseButton
          :title="$t('common.open')"
          class="peer flex w-full bg-transparent p-2 truncate"
          :class="{ 'bg-gray-100 shadow-inner': items.length > 1 && isActive }"
          @click="isActive = !isActive"
          :disabled="disabled"
          v-bind="$attrs"
        >
          <div class="truncate text-sm">
            {{ model?.returnClientName || model?.clientName }}
          </div>
          <div class="flex-1"></div>
          <div
            class="text-white text-xs px-1.5 py-0.5 me-1"
            :class="items.length === 0 ? 'bg-gray-400' : 'bg-gray-700'"
          >
            {{ model?.boxes.length }}
          </div>
          <CaretIcon :active="isActive" :disabled="disabled" />
        </BaseButton>
      </div>
      <div
        class="z-30 absolute mt-0.5 border border-gray-300 bg-white transition-all transform ease-in-out duration-200 w-full overflow-x-auto max-h-64 focus:outline-none shadow-md"
        :class="isActive ? 'opacity-100' : 'opacity-0 collapse'"
      >
        <ul class="cursor-pointer">
          <li
            class="text-sm truncate hover:bg-gray-100 p-2"
            v-for="(item, index) in items"
            :key="index"
            @click="updateModel(item)"
          >
            <div class="flex justify-between">
              {{ item.returnClientName || item.clientName }}
              <span class="bg-gray-700 text-white text-xs px-1.5 py-0.5 me-6">{{ item.boxes.length }}</span>
            </div>
          </li>
        </ul>
      </div>
    </SpinnerLoader>
  </div>
</template>
